import { useEffect, useState } from "react";
import { useGameStore } from "../../store/gameStore";
import { Swords, ChevronDown, ChevronUp, X } from "lucide-react";

// A small pinned recap card for the last fight — separate from the chat scroll so it's still
// glanceable well after the "Combat has ended" message has scrolled out of view. Collapsible,
// and dismissible for the current fight (re-appears fresh the next time combat ends).
export default function CombatLogPanel({ isGm, sidebarCollapsed }) {
  const lastCombatSummary = useGameStore((s) => s.lastCombatSummary);
  const combatActive = useGameStore((s) => s.combat.active);
  const [expanded, setExpanded] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    setHidden(false);
    setExpanded(false);
  }, [lastCombatSummary?.ended_at]);

  if (!lastCombatSummary || combatActive || hidden) return null;

  const { attack_count, hits, misses, crits, total_damage, top_damage } = lastCombatSummary;
  const leftClass = isGm ? (sidebarCollapsed ? "left-16" : "left-[352px]") : "left-4";

  return (
    <div data-testid="combat-log-panel" className={`absolute bottom-4 ${leftClass} z-20 w-56 bg-black/60 backdrop-blur-2xl border border-white/10 rounded-2xl overflow-hidden text-[#f1e0c5]`}>
      <div className="w-full flex items-center gap-2 px-3.5 py-2.5">
        <button data-testid="combat-log-toggle-button" onClick={() => setExpanded((e) => !e)} className="flex items-center gap-2 flex-1 text-left hover:opacity-80 transition-opacity duration-200">
          <Swords className="w-4 h-4 text-[#D90429] shrink-0" />
          <span className="text-xs flex-1">Last Fight Recap</span>
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-[#f1e0c5]/40" /> : <ChevronDown className="w-3.5 h-3.5 text-[#f1e0c5]/40" />}
        </button>
        <button data-testid="combat-log-dismiss-button" onClick={() => setHidden(true)} className="text-[#f1e0c5]/30 hover:text-[#D90429] shrink-0">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      {expanded && (
        <div data-testid="combat-log-details" className="px-3.5 pb-3 space-y-1.5 text-xs border-t border-white/10 pt-2">
          <p className="text-[#f1e0c5]/70">
            {attack_count} attack{attack_count === 1 ? "" : "s"} — {hits} hit{hits === 1 ? "" : "s"}
            {crits ? ` (${crits} crit${crits === 1 ? "" : "s"})` : ""}, {misses} miss{misses === 1 ? "" : "es"}
          </p>
          {total_damage > 0 && <p className="text-[#D90429]">{total_damage} total damage dealt</p>}
          {top_damage?.length > 0 && (
            <div className="pt-1 border-t border-white/10 space-y-0.5">
              <p className="text-[9px] uppercase text-[#f1e0c5]/40">Top Damage</p>
              {top_damage.map(([name, dmg], i) => (
                <p key={i} className="text-[#D4AF37]">{name} — {dmg}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
